// ============================================================================
// Contracts — Covered Facilities panel (Phase 2c, 2026-06-10).
// Shown for account-wide contracts (facility_id IS NULL). Manages the
// contract_facilities junction: a contract can govern N facilities, with an
// optional per-facility rate allocation. Billing remains PER-LINE-ITEM —
// these allocations are display/reference only, with a soft sum-check
// against the contract-level monthly rate.
// ============================================================================

import { useMemo, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { ErrorState } from '../../shared/components';
import { toNumOrNull } from '../../shared/coerce';
import {
  addContractFacility,
  removeContractFacility,
  useContractFacilities,
  useFacilityOptions,
} from './hooks/use-contract-data';
import type { ContractRow } from './types';
import { errorMessage } from '../../shared/errors';

interface FacilitiesPanelProps {
  contract: ContractRow;
  canEdit: boolean;
  onChanged: () => void;
}

const parseAmount = (s: string): number | null => toNumOrNull(s);

export function FacilitiesPanel({ contract, canEdit, onChanged }: FacilitiesPanelProps) {
  const { data: rows, isLoading, error, refetch } = useContractFacilities(
    contract.contract_id,
  );
  const { facilities } = useFacilityOptions();

  const [adding, setAdding] = useState(false);
  const [facilityId, setFacilityId] = useState('');
  const [monthly, setMonthly] = useState('');
  const [hourly, setHourly] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const allocSum = useMemo(
    () => rows.reduce((acc, r) => acc + (r.monthly_rate ?? 0), 0),
    [rows],
  );

  // Account-wide contracts only — single-facility contracts use facility_id.
  if (contract.facility_id) return null;

  const accountFacilities = facilities.filter(
    (f) =>
      f.account_id === contract.account_id &&
      !rows.some((r) => r.facility_id === f.facility_id),
  );
  const sumMismatch =
    contract.monthly_rate != null &&
    allocSum > 0 &&
    Math.abs(allocSum - contract.monthly_rate) > 0.01;

  async function handleAdd() {
    if (!facilityId) return;
    setBusy(true);
    setActionError(null);
    try {
      await addContractFacility({
        contract_id: contract.contract_id,
        facility_id: facilityId,
        monthly_rate: parseAmount(monthly),
        hourly_rate: parseAmount(hourly),
      });
      setFacilityId('');
      setMonthly('');
      setHourly('');
      setAdding(false);
      refetch();
      onChanged();
    } catch (err) {
      setActionError(errorMessage(err, 'Failed to add facility.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(id: string, name: string) {
    if (!window.confirm(`Remove ${name} from this contract?`)) return;
    setBusy(true);
    setActionError(null);
    try {
      await removeContractFacility(id);
      refetch();
      onChanged();
    } catch (err) {
      setActionError(errorMessage(err, 'Failed to remove facility.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-md border border-tcs-border bg-tcs-bg p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-tcs-text">
          Covered facilities {rows.length > 0 && `(${rows.length})`}
        </h3>
        {canEdit && !adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-2 py-1 text-xs font-medium text-tcs-text hover:bg-tcs-bg-muted"
          >
            <Plus className="h-3.5 w-3.5" /> Add facility
          </button>
        )}
      </div>

      {error && <ErrorState title="Couldn't load covered facilities" body={error} />}
      {actionError && <ErrorState title="Action failed" body={actionError} />}

      {isLoading ? (
        <div className="flex items-center gap-2 py-2 text-xs text-tcs-text-muted">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 && !adding ? (
        <p className="text-xs text-tcs-text-muted">
          Account-wide contract — no facilities linked yet.
          {canEdit && ' Use "Add facility" to list the facilities this contract governs.'}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r) => (
            <li
              key={r.contract_facility_id}
              className="flex items-center justify-between gap-2 rounded-md border border-tcs-border/60 bg-tcs-raised px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <span className="font-medium text-tcs-text">
                  {r.facilities?.facility_name ?? r.facility_id}
                </span>
                <span className="ml-2 text-xs text-tcs-text-muted">
                  {r.monthly_rate != null && `$${r.monthly_rate.toLocaleString()}/mo`}
                  {r.monthly_rate != null && r.hourly_rate != null && ' · '}
                  {r.hourly_rate != null && `$${r.hourly_rate.toLocaleString()}/hr`}
                </span>
              </div>
              {canEdit && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    handleRemove(
                      r.contract_facility_id,
                      r.facilities?.facility_name ?? r.facility_id,
                    )
                  }
                  className="shrink-0 rounded p-1 text-tcs-text-muted hover:text-st-danger-fg"
                  aria-label="Remove facility"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {sumMismatch && (
        <p className="mt-2 rounded-md bg-st-warn-bg px-3 py-2 text-[11px] text-st-warn-fg-strong">
          Per-facility monthly allocations sum to ${allocSum.toLocaleString()},
          but the contract monthly rate is $
          {contract.monthly_rate?.toLocaleString()}. Allocations are
          reference-only (billing is per line item), but you may want to
          reconcile them.
        </p>
      )}

      {adding && (
        <div className="mt-3 space-y-2 rounded-md border border-tcs-border/60 bg-tcs-raised p-3">
          <select
            value={facilityId}
            onChange={(e) => setFacilityId(e.target.value)}
            className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
          >
            <option value="">Select a facility on this account…</option>
            {accountFacilities.map((f) => (
              <option key={f.facility_id} value={f.facility_id}>
                {f.facility_name}
                {f.market_region ? ` · ${f.market_region}` : ''}
              </option>
            ))}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Monthly $ (optional)"
              value={monthly}
              onChange={(e) => setMonthly(e.target.value)}
              className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            />
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Hourly $ (optional)"
              value={hourly}
              onChange={(e) => setHourly(e.target.value)}
              className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAdd}
              disabled={!facilityId || busy}
              className="inline-flex items-center gap-1 rounded-md bg-tcs-inverse px-3 py-1.5 text-xs font-semibold text-tcs-inverse-ink disabled:opacity-50"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setActionError(null);
              }}
              className="text-xs text-tcs-text-secondary hover:text-tcs-text"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
